import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';

const RefundModal = ({ show, onHide, booking, refreshBookings }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refundPolicy, setRefundPolicy] = useState('');

  useEffect(() => {
    if (booking) {
      const now = new Date();
      const pickupDate = new Date(booking.pickupDate);
      const hoursDiff = (pickupDate - now) / (1000 * 60 * 60);

      let refundableAmount = 0;
      let policy = '';

      if (hoursDiff > 24) {
        refundableAmount = booking.totalAmount;
        policy = 'Full refund (cancelled >24 hours before pickup)';
      } else if (hoursDiff > 6) {
        refundableAmount = booking.totalAmount * 0.5;
        policy = '50% refund (cancelled 6-24 hours before pickup)';
      } else {
        refundableAmount = 0;
        policy = 'No refund (cancelled <6 hours before pickup)';
      }

      setAmount(refundableAmount.toFixed(2));
      setRefundPolicy(policy);
    }
  }, [booking]);

  const handleRefund = async () => {
    try {
      setLoading(true);
      setError('');

      const refundAmount = parseFloat(amount);
      if (isNaN(refundAmount) || refundAmount < 0) {
        setError('Please enter a valid refund amount.');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('adminToken');

      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/bookings/${booking._id}/refund`,
        { 
          reason, 
          refundAmount: refundAmount 
        },
        { headers: { 'x-auth-token': token } }
      );

      refreshBookings();
      onHide();
    } catch (err) {
      console.error('Error processing refund:', err);
      setError(err.response?.data?.error || 'Failed to process refund');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Process Refund</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="mb-3">
          <h5>Booking Details</h5>
          <p><strong>Customer:</strong> {booking?.customerName}</p>
          <p><strong>Total Paid:</strong> ${booking?.totalAmount.toFixed(2)}</p>
          <p>
            <strong>Payment Method:</strong>{' '}
            {booking?.paymentMethod === 'card' ? 'Credit Card' : 'Cash'}
          </p>
        </div>

        <div className="mb-3 p-3 bg-light rounded">
          <h6>Refund Policy</h6>
          <Badge bg={parseFloat(amount) > 0 ? 'success' : 'danger'}>
            {refundPolicy}
          </Badge>

          <p className="mt-2 mb-0">
            <strong>Calculated Refund Amount:</strong> ${parseFloat(amount || 0).toFixed(2)}
          </p>

          <Form.Group className="mt-3">
            <Form.Label>Refund Amount</Form.Label>
            <Form.Control
              type="number"
              value={amount}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) {
                  setAmount(val);
                }
              }}
              placeholder="Enter refund amount"
              min={0}
              step="0.01"
              required
            />
          </Form.Group>
        </div>

        {booking?.paymentMethod === 'card' ? (
          parseFloat(amount) > 0 ? (
            <Alert variant="info">
              This refund will be processed automatically to the customer's card.
            </Alert>
          ) : (
            <Alert variant="warning">
              No refund will be processed as per cancellation policy.
            </Alert>
          )
        ) : (
          <Alert variant="warning">
            Cash payment — please process refund manually.
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Reason for Cancellation</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for cancellation"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleRefund}
          disabled={loading || amount === '' || (parseFloat(amount) > 0 && !reason.trim())}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            'Confirm Cancellation'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RefundModal;